// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { useCallback } from "react";
import { t } from "@/lib/i18n";
import type { Locale } from "@/lib/types";

export type GeoStatus = "idle" | "prompting" | "granted" | "denied" | "unavailable" | "insecure" | "error";

interface FilterBarProps {
  locale: Locale;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  openNowOnly: boolean;
  onOpenNowChange: (value: boolean) => void;
  selectedType: string;
  onTypeChange: (type: string) => void;
  privateRoomsOnly: boolean;
  onPrivateRoomsChange: (value: boolean) => void;
  // Location-aware controls (optional — only render when wired up).
  geoStatus?: GeoStatus;
  onNearMe?: () => void;
  sortByNearest?: boolean;
  onSortNearestChange?: (value: boolean) => void;
  districts?: { value: string; label: string }[];
  selectedDistrict?: string;
  onDistrictChange?: (value: string) => void;
}

export default function FilterBar({
  locale,
  searchQuery,
  onSearchChange,
  openNowOnly,
  onOpenNowChange,
  selectedType,
  onTypeChange,
  privateRoomsOnly,
  onPrivateRoomsChange,
  geoStatus = "idle",
  onNearMe,
  sortByNearest = false,
  onSortNearestChange,
  districts,
  selectedDistrict = "",
  onDistrictChange,
}: FilterBarProps) {
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value),
    [onSearchChange],
  );

  const types = [
    { value: "karaoke_bar", label: t(locale, "venue_type_karaoke_bar") },
    { value: "private_room", label: t(locale, "venue_type_private_room") },
    { value: "restaurant_karaoke", label: t(locale, "venue_type_restaurant_karaoke") },
    { value: "open_mic", label: t(locale, "venue_type_open_mic") },
  ];

  const located = geoStatus === "granted";
  const nearMeLabel =
    geoStatus === "prompting"
      ? t(locale, "locating")
      : geoStatus === "insecure"
        ? t(locale, "location_insecure")
        : geoStatus === "denied" || geoStatus === "error" || geoStatus === "unavailable"
          ? t(locale, "location_denied")
          : t(locale, "near_me");

  return (
    <div className="space-y-2.5">
      {/* Search */}
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500"
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearch}
          placeholder={t(locale, "search_placeholder")}
          className="w-full pl-9 pr-4 py-2 bg-white/[0.04] border border-white/[0.06] rounded-xl
                     text-sm text-white placeholder-zinc-600
                     focus:outline-none focus:border-violet-500/40 focus:ring-1 focus:ring-violet-500/20
                     transition-all"
        />
      </div>

      {/* Pills */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5 -mx-1 px-1">
        {onNearMe && (
          <button
            onClick={onNearMe}
            data-umami-event="filter-near-me"
            disabled={geoStatus === "prompting"}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                       select-none touch-manipulation flex items-center gap-1 ${located
              ? "bg-sky-500/15 text-sky-300 border border-sky-500/25"
              : "bg-white/[0.04] text-zinc-400 border border-white/[0.06] active:scale-95"
            } ${geoStatus === "prompting" ? "opacity-60" : ""}`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {nearMeLabel}
          </button>
        )}

        {located && onSortNearestChange && (
          <button
            onClick={() => onSortNearestChange(!sortByNearest)}
            data-umami-event="sort-nearest"
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                       select-none touch-manipulation ${sortByNearest
              ? "bg-sky-500/15 text-sky-300 border border-sky-500/25"
              : "bg-white/[0.04] text-zinc-400 border border-white/[0.06] active:scale-95"
            }`}
          >
            {t(locale, "sort_nearest")}
          </button>
        )}

        <button
          onClick={() => onOpenNowChange(!openNowOnly)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                     select-none touch-manipulation ${openNowOnly
            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
            : "bg-white/[0.04] text-zinc-400 border border-white/[0.06] active:scale-95"
          }`}
        >
          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${
            openNowOnly ? "bg-emerald-400" : "bg-zinc-600"
          }`} />
          {t(locale, "open_venues")}
        </button>

        <button
          onClick={() => onPrivateRoomsChange(!privateRoomsOnly)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                     select-none touch-manipulation ${privateRoomsOnly
            ? "bg-violet-500/15 text-violet-300 border border-violet-500/25"
            : "bg-white/[0.04] text-zinc-400 border border-white/[0.06] active:scale-95"
          }`}
        >
          {t(locale, "filter_private_rooms")}
        </button>

        {types.map((ty) => (
          <button
            key={ty.value}
            onClick={() => onTypeChange(selectedType === ty.value ? "" : ty.value)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                       select-none touch-manipulation ${selectedType === ty.value
              ? "bg-violet-500/15 text-violet-300 border border-violet-500/25"
              : "bg-white/[0.04] text-zinc-400 border border-white/[0.06] active:scale-95"
            }`}
          >
            {ty.label}
          </button>
        ))}
      </div>

      {/* District selector — neighborhood-based browsing. */}
      {districts && districts.length > 0 && onDistrictChange && (
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <select
            value={selectedDistrict}
            onChange={(e) => onDistrictChange(e.target.value)}
            className="w-full appearance-none pl-9 pr-9 py-2 bg-white/[0.04] border border-white/[0.06] rounded-xl
                       text-sm text-zinc-300 focus:outline-none focus:border-violet-500/40
                       focus:ring-1 focus:ring-violet-500/20 transition-all cursor-pointer"
          >
            <option value="">{t(locale, "all_districts")}</option>
            {districts.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <svg
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      )}
    </div>
  );
}
